/**
 * The dashboard's read-only view of the content catalogue.
 *
 * The dashboard needs one fact about every lesson in the curriculum at once —
 * how many there are, and whether each one has an unguided stage — which is the
 * opposite of what the player needs (`loadLesson`: one lesson, on demand). So
 * this is a second, much smaller projection of the same JSON rather than a
 * reuse of `TourLesson`: nothing here carries `sections` or `steps`, and the
 * whole catalogue is a few hundred bytes of ids and titles.
 *
 * WHY IT IS ASYNC. `src/player/tour-content.ts` globs the modules NON-EAGERLY
 * on purpose, so the curriculum ships as one lazy chunk per module instead of
 * ~108 kB of JSON inlined into the entry bundle. `DashboardScreen` is reached
 * from `App.tsx` with a static import, so an eager glob here would undo exactly
 * that — the dashboard would pay for every module's full step list to render
 * seven numbers. It loads them instead, and says "loading" while it does.
 *
 * ZH IS EXCLUDED. The `*.zh.json` twins describe the same modules under the
 * same ids (`ordering-essentials`, `ordering-lesson-1`, …), so including them
 * would double every count rather than add lessons — the same reason
 * `findModuleIdFor` reads the EN glob only.
 */

import { useEffect, useState } from 'react'
import { hasUnguidedStage } from '@/player/tour-content'
import type { Module } from '@/schema'

/** One sub-module, reduced to what the dashboard counts and labels. */
export interface CatalogueLesson {
  readonly subModuleId: string
  /** The authored title, e.g. `"Lesson 1: Adding items to your cart"`. */
  readonly title: string
  /**
   * Whether the lesson has an unguided stage — i.e. whether finishing the
   * guided tour alone completes it. Read from content, never from the progress
   * store: see the note on `hasUnguidedStage` in `@/player/tour-content`.
   */
  readonly hasUnguidedStage: boolean
  /** Total step count across all sections — used to compute estimated duration. */
  readonly stepCount: number
}

export interface CatalogueModule {
  /** The JSON module id, e.g. `"ordering-essentials"` — the registry's key. */
  readonly moduleId: string
  /** The authored module title, e.g. `"Ordering Essentials"`. */
  readonly title: string
  readonly lessons: readonly CatalogueLesson[]
}

export type ContentCatalogue = readonly CatalogueModule[]

// EN glob — matches *.json but explicitly NOT *.zh.json.
const moduleLoaders = import.meta.glob<{ default: Module }>([
  '/src/content/modules/*.json',
  '!/src/content/modules/*.zh.json',
])

/** Project one loaded module down to its catalogue entry. Pure. */
export function catalogueModuleOf(mod: Module): CatalogueModule {
  return {
    moduleId: mod.id,
    title: mod.title,
    lessons: mod.subModules.map((subModule) => ({
      subModuleId: subModule.id,
      title: subModule.title,
      hasUnguidedStage: hasUnguidedStage(subModule),
      stepCount: subModule.sections.flatMap((s) => s.steps).length,
    })),
  }
}

/**
 * Every module in the curriculum, in glob order (alphabetical by filename).
 * `deriveDashboard` re-orders them for display — this stays whatever the
 * filesystem says, so a module that nobody has registered still arrives.
 */
export async function loadContentCatalogue(): Promise<ContentCatalogue> {
  const loaded = await Promise.all(Object.values(moduleLoaders).map((load) => load()))
  return loaded.map(({ default: mod }) => catalogueModuleOf(mod))
}

/**
 * What the dashboard does when a content chunk will not load — the lesson
 * device is on store wifi and the module chunks are fetched, not bundled.
 *
 * It resolves to an empty catalogue rather than re-throwing, so the screen
 * lands on a real (if empty) state instead of sitting on "loading" forever
 * with no explanation. Named and exported because it is a policy decision, not
 * an inline shrug.
 */
export function onCatalogueLoadFailure(err: unknown): ContentCatalogue {
  console.warn('dashboard: could not load the content catalogue —', err)
  return []
}

/**
 * The catalogue, or `null` while the content chunks are still in flight.
 *
 * `null` is NOT the same as `[]` to the caller, and that distinction is the
 * point: an empty catalogue means "we looked and there is no training", which
 * is a number the dashboard may honestly show, while `null` means "we do not
 * know yet" and must not be rendered as `0 of 0`. Showing a confident zero
 * before the content lands is the same class of bug as the invented counts this
 * screen replaced.
 *
 * A chunk that fails to load (a lesson device on store wifi) resolves to an
 * empty catalogue rather than leaving the dashboard stuck on "loading" forever.
 */
export function useContentCatalogue(): ContentCatalogue | null {
  const [catalogue, setCatalogue] = useState<ContentCatalogue | null>(null)

  useEffect(() => {
    // Guards against a setState on an unmounted screen — the dashboard is the
    // boot route and a trainee can start a lesson before the chunks land.
    let live = true
    void loadContentCatalogue()
      .catch(onCatalogueLoadFailure)
      .then((next) => {
        if (live) setCatalogue(next)
      })
    return () => {
      live = false
    }
  }, [])

  return catalogue
}
